import { useState } from "react";
import { Copy, Check, Code, Palette, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

function Widget() {
  const [copied, setCopied] = useState(false);
  const [widgetSize, setWidgetSize] = useState("medium");
  const [widgetTheme, setWidgetTheme] = useState("light");

  const sizes = {
    small: { width: "300px", height: "200px" },
    medium: { width: "400px", height: "280px" },
    large: { width: "500px", height: "350px" },
    full: { width: "100%", height: "400px" }
  };

  const themes = {
    light: "bg-white text-gray-900",
    dark: "bg-gray-900 text-white"
  };

  const widgetUrl = `${window.location.origin}/widget-embed?theme=${widgetTheme}`;
  
  const embedCode = `<!-- Is It Taxed Widget -->
<iframe 
  src="${widgetUrl}"
  width="${sizes[widgetSize].width}"
  height="${sizes[widgetSize].height}"
  frameborder="0"
  style="border: 2px solid #e5e7eb; border-radius: 8px;"
  title="UK Vehicle Tax Checker"
></iframe>`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success("Widget code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <a href="/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png" 
                alt="IsItTaxed.co.uk" 
                className="h-12 w-auto"
                style={{ objectFit: 'contain' }}
              />
            </a>
            <a href="/dashboard">
              <Button variant="outline" size="sm">
                Back to Dashboard
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Free Website Widget
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Add a professional vehicle tax checker to your website in seconds. Perfect for garages, dealers, and automotive businesses.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customization Panel */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <Palette className="h-5 w-5" />
                Customize Your Widget
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Size Selection */}
              <div>
                <label className="block text-sm font-semibold mb-3">Widget Size</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(sizes).map((size) => (
                    <button
                      key={size}
                      onClick={() => setWidgetSize(size)}
                      className={`p-3 rounded border-2 text-sm font-medium transition-all ${
                        widgetSize === size
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                      <div className="text-xs text-gray-500 mt-1">
                        {sizes[size].width} × {sizes[size].height}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme Selection */}
              <div>
                <label className="block text-sm font-semibold mb-3">Theme</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setWidgetTheme('light')}
                    className={`p-3 rounded border-2 text-sm font-medium transition-all ${
                      widgetTheme === 'light'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    ☀️ Light
                  </button>
                  <button
                    onClick={() => setWidgetTheme('dark')}
                    className={`p-3 rounded border-2 text-sm font-medium transition-all ${
                      widgetTheme === 'dark'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    🌙 Dark
                  </button>
                </div>
              </div>

              {/* Embed Code */}
              <div>
                <label className="block text-sm font-semibold mb-3">Embed Code</label>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
                    {embedCode}
                  </pre>
                  <Button
                    onClick={copyToClipboard}
                    size="sm"
                    className="absolute top-2 right-2"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Features */}
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-2">✨ Widget Features:</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Official DVLA data</li>
                  <li>✓ Tax & MOT status checks</li>
                  <li>✓ Mobile responsive</li>
                  <li>✓ Fast loading</li>
                  <li>✓ Free forever</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Preview Panel */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <Monitor className="h-5 w-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-100 p-6 rounded-lg flex items-center justify-center min-h-[400px]">
                <iframe
                  src={widgetUrl}
                  width={sizes[widgetSize].width}
                  height={sizes[widgetSize].height}
                  frameBorder="0"
                  style={{ border: '2px solid #e5e7eb', borderRadius: '8px' }}
                  title="UK Vehicle Tax Checker Preview"
                />
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  This is how the widget will appear on your website
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Benefits Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">🚀</div>
              <h3 className="font-bold mb-2">Easy Integration</h3>
              <p className="text-sm text-gray-600">
                Copy & paste the code. No technical skills required.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold mb-2">Add Value</h3>
              <p className="text-sm text-gray-600">
                Help your customers check their vehicle status instantly.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">💼</div>
              <h3 className="font-bold mb-2">Professional</h3>
              <p className="text-sm text-gray-600">
                Modern design that matches any website theme.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-3">Need a custom solution?</h2>
          <p className="mb-6">
            We can create a branded widget specifically for your business.
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Widget;
